import { useRef, useState } from "react";
import { AudioButton } from "./AudioButton";
import { useSettings } from "@/hooks/useSettings";
import { type Accent, playOnline, speak } from "@/lib/audio";

interface PronounceProps {
  word: string;
  size?: number;
}

const ACCENTS: readonly { key: Accent; badge: string; label: string }[] = [
  { key: "uk", badge: "UK", label: "British English" },
  { key: "us", badge: "US", label: "American English" },
];

/**
 * Pronunciation controls for a word. The shape follows the audio source in
 * Settings: one speaker for the browser voice, a UK + US pair for the online
 * dictionary, nothing at all when audio is off.
 */
export function Pronounce({ word, size = 32 }: PronounceProps) {
  const { settings } = useSettings();
  const [busy, setBusy] = useState<Accent | null>(null);
  // Rapid taps overlap: an older clip resolves (cut short) after the newer one
  // started. Only the latest request is allowed to clear the playing state.
  const latest = useRef(0);

  if (settings.audioSource === "off") return null;

  if (settings.audioSource === "browser") {
    return <AudioButton size={size} label="Tap to hear" onPlay={() => speak(word, settings.audio)} />;
  }

  return (
    <span className="pronounce">
      {ACCENTS.map(({ key, badge, label }) => (
        <AudioButton
          key={key}
          size={size}
          badge={badge}
          label={label}
          busy={busy === key}
          onPlay={async () => {
            const id = ++latest.current;
            setBusy(key);
            await playOnline(word, key);
            if (latest.current === id) setBusy(null);
          }}
        />
      ))}
    </span>
  );
}
